import { Chip, SxProps, Theme, Box } from '@mui/material';

import { BackrsIcon, BackrsIcons } from './BackrsIcon';
import { appColors } from '../assets/appColors';

type BackrsChipProps = Readonly<{
  label: string;
  variant:
    | {
        type: 'LARGE_TEXT';
        backgroundColor: 'BLUE' | 'GRAY' | 'LIGHT_BLUE';
      }
    | {
        type: 'SMALL_TEXT';
        leftIcon?: {
          name: BackrsIcons;
          alt: string;
        };
      }
    | {
        type: 'TINY_TEXT';
      };
  onClick?: () => void;
  isSelected?: boolean;
  sx?: SxProps<Theme>;
}>;

const getChipStyle = (variant: BackrsChipProps['variant']): SxProps<Theme> => {
  switch (variant.type) {
    case 'LARGE_TEXT':
      return largeChipStyle;
    case 'TINY_TEXT':
      return tinyChipStyle;
    case 'SMALL_TEXT':
    default:
      return smallChipStyle;
  }
};

export const BackrsChip = ({ label, variant, onClick, isSelected = true, sx }: BackrsChipProps) => {
  const backgroundColors: Record<string, string> = {
    LARGE_TEXT_BLUE: appColors.color.blue1,
    LARGE_TEXT_GRAY: appColors.neutral.lightGray,
    LARGE_TEXT_LIGHT_BLUE: appColors.color.blue3,
    SMALL_TEXT: appColors.color.blue4,
    TINY_TEXT: appColors.color.blue4,
  };

  const bgColor =
    variant.type === 'LARGE_TEXT'
      ? backgroundColors[`LARGE_TEXT_${variant.backgroundColor}`]
      : backgroundColors[variant.type];

  return (
    <Chip
      onClick={onClick}
      label={label}
      sx={
        {
          ...getChipStyle(variant),
          cursor: onClick ? 'pointer' : 'default',
          backgroundColor: bgColor,
          '& .MuiChip-icon': muiChipIconSx,
          '& .MuiChip-label': {
            ...muiLabelSx,
            marginLeft: variant.type === 'SMALL_TEXT' && variant.leftIcon ? 1 : 0,
          },
          opacity: isSelected ? 1 : 0.5,
          ...sx,
        } as SxProps<Theme>
      }
      {...(variant.type === 'SMALL_TEXT' &&
        variant.leftIcon && {
          icon: (
            <Box>
              <BackrsIcon name={variant.leftIcon.name} alt={variant.leftIcon.alt} />
            </Box>
          ),
        })}
    />
  );
};

const largeChipStyle: SxProps<Theme> = {
  fontSize: 16,
  lineHeight: '20px',
  fontWeight: 'bold',
  color: appColors.neutral.white,
  px: 3,
  pb: 0.25,
  boxSizing: 'border-box',
};

const smallChipStyle: SxProps<Theme> = {
  fontSize: 13,
  lineHeight: 1.3,
  fontWeight: 'bold',
  color: appColors.color.navy3,
  px: 2,
  py: 2.25,
  borderRadius: 2,
};

const tinyChipStyle: SxProps<Theme> = {
  fontSize: 13,
  lineHeight: 1.3,
  fontWeight: 'bold',
  color: appColors.color.blue1,
  px: 2,
  borderRadius: 100,
};

const muiLabelSx: SxProps<Theme> = {
  padding: 0,
  textOverflow: 'ellipsis',
};

const muiChipIconSx: SxProps<Theme> = {
  ml: 0,
  mr: 0,
  display: 'flex',
};
